import { useRef } from 'react';
import { isMobile } from 'react-device-detect';
import parser from '../../parsers/DataTable/leadDataTableParser';
import DataTable from './DataTable';
import NoData from './InvoicesPurchases/NoData';
import DateFormatter from './CellFormatters/DateFormatter';
import { FILTER_TYPE_DATE, FILTER_TYPE_DATE_RANGE, FILTER_TYPE_SELECT, FILTER_TYPE_TEXT } from '../../constants/filterTypes';
import { MATCH_TYPE_IN, MATCH_TYPE_LIKE, MATCH_TYPE_RAW_DQL } from '../../constants/filterMatchTypes';
import StatusFormatter from './CellFormatters/StatusFormatter';
import MoneyFormatter from './CellFormatters/MoneyFormatter';
import MobileDataTable from './MobileDataTable';
import MoneyCurrencyFormatter from './CellFormatters/MoneyCurrencyFormatter';
import WideTextFormatter from './CellFormatters/WideTextFormatter';
import TextFormatter from './CellFormatters/TextFormatter';
import { useWindowSize } from '../../helpers/windowSizeHook';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { selectProducts } from '../../store/selectors/products';
import { selectRepaymentPeriods } from '../../store/selectors/repaymentPeriods';
import { selectUserInfo } from '../../store/selectors/userInfo';
import { selectAgents } from '../../store/selectors/agents';
import { selectCustomers } from '../../store/selectors/customers';
import RepaymentPeriodFormatter from './CellFormatters/RepaymentPeriodFormatter';

const GainedFinancialDocumentsDatatable = () => {
  const { formatMessage } = useIntl();
  const products = useSelector(selectProducts);
  const repaymentPeriods = useSelector(selectRepaymentPeriods);
  const userInfo = useSelector(selectUserInfo);
  const agents = useSelector(selectAgents);
  const customers = useSelector(selectCustomers);
  const windowSize = useWindowSize();
  const datatableRef = useRef();
  const url = '/financial-document-grid';
  const grid = 'financial-documents';
  const dispatch = useDispatch();

  const mobileColumns = [
    {
      name: formatMessage({ id: 'Product' }),
      selector: 'product',
    },
    {
      name: formatMessage({ id: 'Date added' }),
      cell: (row) => <DateFormatter date={row.dateAdded} />,
    },
    {
      name: formatMessage({ id: 'Document number' }),
      selector: 'documentNumber',
    },
    {
      name: formatMessage({ id: 'Amount' }),
      cell: (row) => <MoneyCurrencyFormatter amount={row.amount} currency={row.currency} />,
    },
    {
      name: formatMessage({ id: 'Repayment period' }),
      cell: (row) => (null !== row.repaymentPeriod ? <RepaymentPeriodFormatter value={row.repaymentPeriod} /> : null),
    },
    {
      name: formatMessage({ id: 'Update total value' }),
      cell: (row) =>
        null !== row.updateTotalValueAmount && null !== row.updateTotalValueCurrency ? (
          <MoneyCurrencyFormatter amount={row.updateTotalValueAmount} currency={row.updateTotalValueCurrency} />
        ) : null,
    },
    {
      name: formatMessage({ id: 'Status' }),
      cell: (row) => <StatusFormatter status={row.status} />,
    },
  ];

  const columns = [
    {
      name: formatMessage({ id: 'Customer name' }),
      selector: 'customerName',
      sortable: true,
      grow: 1.7,
      maxWidth: !windowSize.width < 1600 ? '100%' : '350px',
      cell: (row) => <WideTextFormatter text={row.customerName} />,
    },
    {
      name: formatMessage({ id: 'Product' }),
      selector: 'product',
      sortable: true,
      maxWidth: !windowSize.width < 1600 ? '100%' : '200px',
      cell: (row) => <WideTextFormatter text={row.product} />,
    },
    {
      name: formatMessage({ id: 'Date added' }),
      selector: 'dateAdded',
      sortable: true,
      cell: (row) => <DateFormatter date={row.dateAdded} />,
      maxWidth: !windowSize.width < 1600 ? '100%' : '140px',
    },
    {
      name: formatMessage({ id: 'Funding date' }),
      selector: 'fundingDate',
      sortable: true,
      cell: (row) => <DateFormatter date={row.fundingDate} />,
      maxWidth: !windowSize.width < 1600 ? '100%' : '140px',
    },
    {
      name: formatMessage({ id: 'Document number' }),
      selector: 'documentNumber',
      sortable: true,
      grow: 1.2,
      maxWidth: !windowSize.width < 1600 ? '100%' : '160px',
      cell: (row) => <WideTextFormatter text={row.documentNumber} />,
    },
    {
      name: formatMessage({ id: 'Amount' }),
      selector: 'amount',
      sortable: true,
      grow: 0.8,
      maxWidth: !windowSize.width < 1600 ? '100%' : '150px',
      right: true,
      cell: (row) => <MoneyFormatter number={row.amount} />,
    },
    {
      name: formatMessage({ id: 'Currency' }),
      selector: 'currency',
      sortable: true,
      grow: 0.4,
      maxWidth: !windowSize.width < 1600 ? '100%' : '30px',
      cell: (row) => <WideTextFormatter text={row.currency} />,
    },
    {
      name: formatMessage({ id: 'Repayment period' }),
      selector: 'repaymentPeriod',
      sortable: false,
      maxWidth: !windowSize.width < 1600 ? '100%' : '100px',
      cell: (row) => (null !== row.repaymentPeriod ? <RepaymentPeriodFormatter value={row.repaymentPeriod} /> : null),
    },
    {
      name: formatMessage({ id: 'Update total value' }),
      selector: 'updateTotalAmount',
      sortable: false,
      maxWidth: !windowSize.width < 1600 ? '100%' : '150px',
      cell: (row) =>
        null !== row.updateTotalValueAmount && null !== row.updateTotalValueCurrency ? (
          <MoneyCurrencyFormatter amount={row.updateTotalValueAmount} currency={row.updateTotalValueCurrency} />
        ) : null,
    },
    {
      name: formatMessage({ id: 'Status' }),
      selector: 'status',
      sortable: true,
      maxWidth: !windowSize.width < 1600 ? '100%' : '130px',
      cell: (row) => <StatusFormatter status={row.status} />,
    },
  ];

  const prepareProductOptions = () => {
    let defaults = [
      {
        label: formatMessage({ id: 'NANO' }),
        value: formatMessage({ id: 'NANO' }),
      },
      {
        label: formatMessage({ id: 'FACTORING' }),
        value: formatMessage({ id: 'FACTORING' }),
      },
      {
        label: formatMessage({ id: 'PURCHASE_FINANCE' }),
        value: formatMessage({ id: 'PURCHASE_FINANCE' }),
      },
    ];

    if (products.data !== undefined) {
      products.data.financialDocuments.forEach((product) => {
        if (!defaults.find((e) => e.value === product)) {
          defaults.push({
            label: product,
            value: product,
          });
        }
      });
    }

    return defaults;
  };

  const prepareRepaymentPeriodsOptions = () => {
    let defaults = [
      {
        label: formatMessage({ id: 'INSTALLMENTS' }),
        value: 'INSTALLMENTS',
      },
      {
        label: formatMessage({ id: 'DAYS' }),
        value: 'DAYS',
      },
      {
        label: formatMessage({ id: 'MONTHS' }),
        value: 'MONTHS',
      },
    ];

    if (repaymentPeriods.data !== undefined) {
      repaymentPeriods.data.forEach((repaymentPeriod) => {
        if (!defaults.find((e) => e.value === repaymentPeriod)) {
          defaults.push({
            label: <RepaymentPeriodFormatter value={repaymentPeriod} />,
            value: repaymentPeriod.value + ' ' + repaymentPeriod.type,
          });
        }
      });
    }

    return defaults;
  };

  const filters = [
    {
      type: FILTER_TYPE_SELECT,
      field: 'customerNip',
      matchType: MATCH_TYPE_IN,
      placeholder: 'Type name or Nip',
      label: formatMessage({ id: 'Company' }),
      options: customers?.data || [],
    },
    {
      type: FILTER_TYPE_SELECT,
      field: 'product',
      matchType: MATCH_TYPE_IN,
      label: formatMessage({ id: 'Product' }),
      options: prepareProductOptions(),
    },
    {
      type: FILTER_TYPE_TEXT,
      field: 'documentNumber',
      matchType: MATCH_TYPE_LIKE,
      label: formatMessage({ id: 'Document number' }),
      placeholder: formatMessage({ id: 'Enter invoice or loan number' }),
    },
    {
      type: FILTER_TYPE_SELECT,
      field: 'status',
      matchType: MATCH_TYPE_IN,
      label: formatMessage({ id: 'Status' }),
      options: [
        {
          label: formatMessage({ id: 'processing' }),
          value: 'processing',
        },
        {
          label: formatMessage({ id: 'agreed' }),
          value: 'agreed',
        },
        {
          label: formatMessage({ id: 'signed' }),
          value: 'signed',
        },
        {
          label: formatMessage({ id: 'paid' }),
          value: 'paid',
        },
        {
          label: formatMessage({ id: 'financed' }),
          value: 'financed',
        },
        {
          label: formatMessage({ id: 'to_confirm' }),
          value: 'to_confirm',
        },
        {
          label: formatMessage({ id: 'canceled' }),
          value: 'canceled',
        },
        {
          label: formatMessage({ id: 'terminated' }),
          value: 'terminated',
        },
        {
          label: formatMessage({ id: 'rejected' }),
          value: 'rejected',
        },
        {
          label: formatMessage({ id: 'uncompleted' }),
          value: 'uncompleted',
        },
      ],
    },
    {
      type: FILTER_TYPE_DATE_RANGE,
      groupedBy: 'dateAdded',
      availableFields: ['dateAddedFrom', 'dateAddedTo'],
      collection: [
        {
          label: formatMessage({ id: 'Date added' }),
          type: FILTER_TYPE_DATE,
          field: 'dateAddedFrom',
          matchType: MATCH_TYPE_RAW_DQL,
          placeholder: formatMessage({ id: 'From' }),
          dateFrom: true,
          expression: {
            value: 'searched.dateAdded >= :dateAddedFrom',
          },
        },
        {
          type: FILTER_TYPE_DATE,
          field: 'dateAddedTo',
          matchType: MATCH_TYPE_RAW_DQL,
          placeholder: formatMessage({ id: 'To' }),
          dateTo: true,
          expression: {
            value: 'searched.dateAdded <= :dateAddedTo',
          },
        },
      ],
    },
    {
      type: FILTER_TYPE_DATE_RANGE,
      groupedBy: 'fundingDate',
      availableFields: ['fundingDateFrom', 'fundingDateTo'],
      collection: [
        {
          label: formatMessage({ id: 'Funding date' }),
          type: FILTER_TYPE_DATE,
          field: 'fundingDateFrom',
          matchType: MATCH_TYPE_RAW_DQL,
          placeholder: formatMessage({ id: 'From' }),
          dateFrom: true,
          expression: {
            value: 'searched.fundingDate >= :fundingDateFrom',
          },
        },
        {
          type: FILTER_TYPE_DATE,
          field: 'fundingDateTo',
          matchType: MATCH_TYPE_RAW_DQL,
          placeholder: formatMessage({ id: 'To' }),
          dateTo: true,
          expression: {
            value: 'searched.fundingDate <= :fundingDateTo',
          },
        },
      ],
    },
    {
      type: FILTER_TYPE_SELECT,
      field: 'repaymentPeriod',
      matchType: MATCH_TYPE_RAW_DQL,
      label: formatMessage({ id: 'Repayment period' }),
      options: prepareRepaymentPeriodsOptions(),
      expression: {
        value: "CONCAT(searched.repaymentPeriodValue, ' ', searched.repaymentPeriodType) LIKE :repaymentPeriod",
        wildcard: true,
      },
    },
    {
      type: FILTER_TYPE_TEXT,
      field: 'partnerItemId',
      matchType: MATCH_TYPE_LIKE,
      label: formatMessage({ id: 'Partner item id' }),
      placeholder: formatMessage({
        id: 'Enter partner item id',
      }),
    },
  ];

  const addExtraFieldsForNetworkMaster = () => {
    columns.push({
      name: formatMessage({ id: 'Agent' }),
      selector: 'agentName',
      sortable: true,
      maxWidth: '160px',
      cell: (row) => <WideTextFormatter text={row.agentName} />,
    });

    mobileColumns.push({
      name: formatMessage({ id: 'Agent' }),
      cell: (row) => <TextFormatter text={row.agentName} />,
    });

    filters.push({
      type: FILTER_TYPE_SELECT,
      field: 'agentId',
      matchType: MATCH_TYPE_IN,
      label: formatMessage({ id: 'Agent' }),
      options: agents?.data ? agents.data : [],
    });
  };

  if (true === userInfo.data?.isMaster) {
    addExtraFieldsForNetworkMaster();
  }

  const dataTable = isMobile ? (
    <MobileDataTable
      dispatch={dispatch}
      url={url}
      filters={filters}
      recordLines={mobileColumns}
      recordHeaderKey='customerName'
      isSummary={true}
      reloadGrid={grid}
      defaultSortField='dateAdded'
    />
  ) : (
    <DataTable
      ref={datatableRef}
      url={url}
      columns={columns}
      filters={filters}
      parser={parser}
      noDataComponent={<NoData />}
      isSummary={true}
      reloadGrid={grid}
      defaultSortField='dateAdded'
    />
  );

  return dataTable;
};

export default GainedFinancialDocumentsDatatable;
